import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {getTreatmentRecords, createTreatmentRecord, updateTreatmentRecord} from "../../api/pond";
import {useTranslation} from "react-i18next";

interface TreatmentTabProps {
  pondId: string;
}

const TreatmentTab: React.FC<TreatmentTabProps> = ({pondId}) => {
  const {t} = useTranslation();
  const [treatmentRecords, setTreatmentRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [formData, setFormData] = useState({
    treatedAt: "",
    treatment: "",
    notes: "",
  });

  useEffect(() => {
    fetchTreatmentRecords();
  }, [pondId]);

  const fetchTreatmentRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTreatmentRecords(pondId);
      setTreatmentRecords(data.data);
    } catch (err) {
      setError("Failed to load treatment records");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (record?: any) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        treatedAt: record.treatedAt ? new Date(record.treatedAt).toISOString().slice(0, 16) : "",
        treatment: record.treatment,
        notes: record.notes || "",
      });
    } else {
      setEditingRecord(null);
      setFormData({
        treatedAt: new Date().toISOString().slice(0, 16),
        treatment: "",
        notes: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  const handleSave = async () => {
    try {
      if (editingRecord) {
        await updateTreatmentRecord(editingRecord.id, formData);
      } else {
        await createTreatmentRecord(pondId, formData);
      }
      handleCloseModal();
      fetchTreatmentRecords();
    } catch (err) {
      setError("Failed to save treatment record");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{t("Treatment Records")}</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenModal()}>
          {t("Add Treatment")}
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{overflowX: "auto", maxWidth: "100%"}}>
        <Table size="small" sx={{minWidth: 600}}>
          <TableHead>
            <TableRow>
              <TableCell>{t("Date")}</TableCell>
              <TableCell>{t("Treatment Type")}</TableCell>
              <TableCell>{t("Product")}</TableCell>
              <TableCell>{t("Dosage")}</TableCell>
              <TableCell>{t("Duration")}</TableCell>
              <TableCell>{t("Remark")}</TableCell>
              <TableCell>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {treatmentRecords.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.treatmentDate ? new Date(record.treatmentDate).toLocaleDateString() : "-"}</TableCell>
                <TableCell>{record.treatmentType}</TableCell>
                <TableCell>{record.product}</TableCell>
                <TableCell>{record.dosage}</TableCell>
                <TableCell>{record.duration}</TableCell>
                <TableCell sx={{maxWidth: 150, wordWrap: "break-word"}}>{record.remark || ""}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => handleOpenModal(record)}>
                    {t("Edit")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRecord ? t("Edit Treatment Record") : t("Add Treatment Record")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t("Treated At")}
              type="datetime-local"
              value={formData.treatedAt}
              onChange={(e) => setFormData({...formData, treatedAt: e.target.value})}
              InputLabelProps={{shrink: true}}
            />
            <TextField
              label={t("Treatment")}
              value={formData.treatment}
              onChange={(e) => setFormData({...formData, treatment: e.target.value})}
              placeholder={t("e.g., Antibiotic treatment, Vitamin supplement, etc.")}
            />
            <TextField
              label={t("Notes")}
              multiline
              rows={4}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder={t("Additional details about the treatment, dosage, application method, etc.")}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t("Cancel")}</Button>
          <Button onClick={handleSave} variant="contained">
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TreatmentTab;
